// src/components/ResourcesSection.jsx
import React, { useState, useRef, useEffect } from 'react';
import { ExternalLink, Plus, Trash2, X } from 'lucide-react';
import { normalizeUrl } from '../utils/utils';

function ResourcesSection({ resources, onAddResource, onDeleteResource }) {
  const [showAddForm, setShowAddForm] = useState(false);
  const [labelInput, setLabelInput] = useState('');
  const [urlInput, setUrlInput] = useState('');
  const urlInputRef = useRef(null);

  useEffect(() => {
    if (showAddForm && urlInputRef.current) {
      urlInputRef.current.focus();
    }
  }, [showAddForm]);

  const handleAdd = (e) => {
    e.preventDefault();
    if (urlInput.trim()) {
      // Normalize URL to ensure it has a protocol
      const normalizedUrl = normalizeUrl(urlInput.trim());
      onAddResource(labelInput.trim(), normalizedUrl);
      setLabelInput('');
      setUrlInput('');
      setShowAddForm(false);
    }
  };

  const handleCancel = () => {
    setLabelInput('');
    setUrlInput('');
    setShowAddForm(false);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
          Resources
        </h3>
        {!showAddForm && (
          <button
            onClick={() => setShowAddForm(true)}
            className="flex items-center gap-1 px-2 py-1 text-xs bg-primary/10 hover:bg-primary/20 text-primary rounded-md transition-colors"
            aria-label="Add resource"
          >
            <Plus className="w-3 h-3" />
            Add Link
          </button>
        )}
      </div>

      {/* Resources List */}
      {resources.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {resources.map((resource) => (
            <div
              key={resource.id}
              className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors group"
            >
              <a
                href={normalizeUrl(resource.url)}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 text-blue-700 dark:text-blue-300 hover:text-blue-900 dark:hover:text-blue-100 text-sm font-medium"
              >
                <ExternalLink className="w-3.5 h-3.5" />
                <span>{resource.label}</span>
              </a>
              <button
                onClick={() => onDeleteResource(resource.id)}
                className="p-0.5 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/30 rounded opacity-0 group-hover:opacity-100 transition-opacity"
                aria-label="Delete resource"
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Add Form */}
      {showAddForm && (
        <div className="p-3 bg-gray-50 dark:bg-gray-900/50 rounded-md border border-gray-200 dark:border-gray-700 space-y-2 animate-in fade-in slide-in-from-top-2 duration-200">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-gray-700 dark:text-gray-300">Add Resource</span>
            <button
              onClick={handleCancel}
              className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              aria-label="Cancel"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
          <input
            type="text"
            value={labelInput}
            onChange={(e) => setLabelInput(e.target.value)}
            placeholder="Label (optional, e.g., GitHub Repo)"
            className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          />
          <input
            ref={urlInputRef}
            type="url"
            value={urlInput}
            onChange={(e) => setUrlInput(e.target.value)}
            placeholder="URL (required, e.g., https://github.com/...)"
            className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            required
          />
          <div className="flex gap-2">
            <button
              onClick={handleAdd}
              disabled={!urlInput.trim()}
              className="flex-1 px-3 py-1.5 text-xs bg-primary hover:bg-primary/90 text-white rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Add
            </button>
            <button
              onClick={handleCancel}
              className="px-3 py-1.5 text-xs bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-md transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {resources.length === 0 && !showAddForm && (
        <p className="text-xs text-gray-500 dark:text-gray-400 italic">No resources added yet.</p>
      )}
    </div>
  );
}

export default ResourcesSection;

